import Chainable from "@repo/utils/Chainable";
import UFunction from "@repo/utils/UFunction";
import UNumber from "@repo/utils/UNumber";

import Animation from "./Animation.mjs";
import Css from "./Css.mjs";
import UEventTarget from "./UEventTarget.mjs";

namespace UHTMLElement {
  export function parents(element: HTMLElement) {
    const parents: HTMLElement[] = [];

    (function getParents(el: HTMLElement | null) {
      if (!el) return;

      parents.push(el);
      getParents(el.parentElement);
    })(element);

    return parents;
  }

  export function scrollParents(element: HTMLElement) {
    const parents: HTMLElement[] = [];

    (function getParents(el: HTMLElement | null) {
      if (!el) return;

      if (el.scrollHeight > el.clientHeight) parents.push(el);

      getParents(el.parentElement);
    })(element);

    return parents;
  }
  scrollParents.listen = (element: HTMLElement, handler: () => void) => {
    const parents = UHTMLElement.scrollParents(element);
    for (const parent of parents) {
      parent.addEventListener("scroll", handler);
    }
    return {
      unlisten() {
        for (const parent of parents) {
          parent.removeEventListener("scroll", handler);
        }
      },
    };
  };
  export function computeTotalTransitionMs(element: HTMLElement) {
    const computed = getComputedStyle(element);

    return (
      Css.parseTime(computed.transitionDuration) +
      Css.parseTime(computed.transitionDelay)
    );
  }

  export const scrollTo = Chainable.create(
    Chainable.wrapFactory(
      ({ durationMs = 0, easeFn = Animation.ease.sine.out }) =>
        (
          scrollable: HTMLElement,
          target:
            | { top?: number; left?: number }
            | {
                element: HTMLElement;
                offset?: { top?: number; left?: number };
              },
        ) => {
          if ("element" in target) {
            const scrollableRect = scrollable.getBoundingClientRect();
            const elementRect = target.element.getBoundingClientRect();
            target = {
              top:
                elementRect.top -
                scrollableRect.top +
                scrollable.scrollTop -
                (target.offset?.top ?? 0),
              left:
                elementRect.left -
                scrollableRect.left +
                scrollable.scrollLeft -
                (target.offset?.left ?? 0),
            };
          }

          if (durationMs === 0) {
            if (target.left != null) scrollable.scrollLeft = target.left;
            if (target.top != null) scrollable.scrollTop = target.top;
            return Object.assign(Promise.resolve(), { cancel: UFunction.noop });
          }

          const startLeft = scrollable.scrollLeft;
          const startTop = scrollable.scrollTop;

          const tween = Animation.tween.ease(easeFn)(durationMs, (progress) => {
            if (target.left != null)
              scrollable.scrollLeft = UNumber.progress.toValue(progress, [
                startLeft,
                target.left,
              ]);

            if (target.top != null)
              scrollable.scrollTop = UNumber.progress.toValue(progress, [
                startTop,
                target.top,
              ]);
          });

          tween.catch(UFunction.noop).then(
            UEventTarget.wrap(scrollable).on(["wheel", "touchstart"], () => {
              tween.cancel();
            }).off,
          );

          return tween;
        },
    ),
    {
      for: (durationMs: number) => ({ durationMs }),
      ease: (easeFn: (x: number) => number) => ({ easeFn }),
    },
  );
}

export default UHTMLElement;
